import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import { Tabs, TabsList, TabsTrigger, TabsContent } from '../components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from '../components/ui/dialog';
import { api } from '../lib/api';
import { useAuth } from '../context/AuthContext';
import { toast } from 'sonner';
import {
  Store, Coins, DollarSign, Image as ImageIcon, Sparkles, Loader2, ShoppingBag,
  Package, Copy, Check, Trash2, Plus, ExternalLink, Tag, Filter, Rocket
} from 'lucide-react';

const CATEGORIES = [
  { v: 'prompt_pack', label: 'Prompt Pack', Icon: Sparkles },
  { v: 'artwork',     label: 'AI Artwork', Icon: ImageIcon },
  { v: 'template',    label: 'Template',   Icon: Package },
  { v: 'workflow',    label: 'Workflow',   Icon: Store },
  { v: 'assistant',   label: 'AI Assistant', Icon: Sparkles },
];

function priceDisplay(p) {
  return p.currency === 'usd'
    ? <span className="inline-flex items-center gap-1 font-bold text-emerald-300"><DollarSign className="w-3.5 h-3.5" />{p.price_usd?.toFixed(2)}</span>
    : <span className="inline-flex items-center gap-1 font-bold text-amber-300"><Coins className="w-3.5 h-3.5" />{p.price_jxp}</span>;
}

function ListingCard({ p, onOpen }) {
  const C = CATEGORIES.find(c => c.v === p.category) || CATEGORIES[0];
  return (
    <button
      data-testid={`market-listing-${p.id}`}
      onClick={() => onOpen(p)}
      className="group text-left glass rounded-2xl overflow-hidden border border-white/10 hover:border-violet-400/50 transition-all hover:scale-[1.01] active:scale-[0.99]"
    >
      <div className="relative aspect-[4/3] bg-gradient-to-br from-slate-800 to-slate-900">
        {p.preview_url ? (
          <img src={p.preview_url} alt="" className="w-full h-full object-cover" />
        ) : (
          <div className="w-full h-full grid place-items-center text-slate-700">
            <C.Icon className="w-16 h-16" />
          </div>
        )}
        <div className="absolute top-2 left-2 flex items-center gap-1">
          <div className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-950/80 border border-white/10">
            <C.Icon className="w-3 h-3" /> {C.label}
          </div>
          {p.is_boosted && (
            <div data-testid={`market-boost-badge-${p.id}`}
                 className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-amber-500/90 border border-amber-300/60 text-slate-950 shadow-lg shadow-amber-500/30 animate-pulse">
              <Rocket className="w-3 h-3" /> Boosted
            </div>
          )}
        </div>
        <div className="absolute top-2 right-2 inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold bg-slate-950/80 border border-white/10">
          {priceDisplay(p)}
        </div>
      </div>
      <div className="p-3 space-y-1">
        <div className="font-display font-bold text-sm truncate">{p.title}</div>
        <div className="text-[11px] text-slate-400 truncate">by @{p.seller_username}</div>
        <div className="text-[10px] text-slate-500 flex items-center gap-1">
          <ShoppingBag className="w-3 h-3" /> {p.sales || 0} sold
        </div>
      </div>
    </button>
  );
}

function PurchaseDialog({ product, onClose, onPurchased, user }) {
  const [busy, setBusy] = useState(false);
  const [content, setContent] = useState(null);  // set after purchase (prompt body for prompt packs)
  const [copied, setCopied] = useState(false);

  const buy = async () => {
    setBusy(true);
    try {
      if (product.currency === 'jxp') {
        const { data } = await api.post('/marketplace/purchase', { product_id: product.id });
        toast.success(data.already_owned ? 'Already owned — content unlocked' : `Purchased! ${product.price_jxp} JXP charged.`);
        setContent(data.content);
        onPurchased && onPurchased();
      } else {
        const { data } = await api.post('/marketplace/checkout', { product_id: product.id, origin_url: window.location.origin });
        window.location.href = data.checkout_url;
      }
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Purchase failed');
      setBusy(false);
    }
  };

  const copy = async () => {
    try { await navigator.clipboard.writeText(content || ''); setCopied(true); setTimeout(() => setCopied(false), 1500); }
    catch { /* noop */ }
  };

  const isOwner = user?.id === product.seller_id;
  const isPurchased = product.is_purchased || content != null;

  return (
    <Dialog open={!!product} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="market-purchase-dialog" className="max-w-md">
        <DialogHeader>
          <DialogTitle className="font-display">{product.title}</DialogTitle>
          <DialogDescription className="text-xs">
            {product.category.replace('_', ' ')} · by @{product.seller_username} · {product.sales || 0} sold
          </DialogDescription>
        </DialogHeader>
        {product.preview_url && (
          <img src={product.preview_url} alt="" className="w-full rounded-xl aspect-video object-cover" />
        )}
        <div className="text-sm whitespace-pre-wrap text-slate-300">{product.description}</div>
        <div className="flex items-center justify-between glass p-3 rounded-xl">
          <div className="text-xs text-slate-400">Price</div>
          <div className="text-xl font-black">{priceDisplay(product)}</div>
        </div>
        {isPurchased && content && (
          <div className="rounded-xl bg-emerald-500/10 border border-emerald-500/30 p-3 space-y-2">
            <div className="flex items-center justify-between">
              <div className="text-xs font-bold text-emerald-200">✨ Unlocked content</div>
              <Button data-testid="market-copy-content" size="sm" variant="outline" onClick={copy}>
                {copied ? <><Check className="w-3 h-3 mr-1" /> Copied</> : <><Copy className="w-3 h-3 mr-1" /> Copy</>}
              </Button>
            </div>
            <pre className="text-xs whitespace-pre-wrap text-slate-200 max-h-60 overflow-y-auto">{content}</pre>
          </div>
        )}
        <DialogFooter>
          {isOwner ? (
            <Button variant="outline" disabled>This is your listing</Button>
          ) : isPurchased ? (
            <Button data-testid="market-close-purchased" onClick={onClose} className="gradient-brand text-white w-full">Done</Button>
          ) : (
            <Button data-testid="market-buy-btn" onClick={buy} disabled={busy} className="gradient-brand text-white w-full">
              {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Processing…</> : <>Buy for {product.currency === 'usd' ? `$${product.price_usd?.toFixed(2)}` : `${product.price_jxp} JXP`}</>}
            </Button>
          )}
        </DialogFooter>
        {!isOwner && !isPurchased && (
          <div className="text-[10px] text-slate-500 text-center">
            Jixxeo takes a 10% platform fee. The rest goes to the creator.
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}

function CreateListingDialog({ open, onClose, onCreated }) {
  const [form, setForm] = useState({
    title: '', description: '', category: 'prompt_pack', currency: 'jxp',
    price_jxp: 100, price_usd: 2.99, preview_url: '', content: '',
  });
  const [busy, setBusy] = useState(false);
  const [uploadingCover, setUploadingCover] = useState(false);

  const pickCover = async (file) => {
    if (!file) return;
    if (!file.type?.startsWith('image/')) { toast.error('Please pick an image'); return; }
    if (file.size > 5 * 1024 * 1024) { toast.error('Max 5 MB'); return; }
    setUploadingCover(true);
    try {
      const fd = new FormData();
      fd.append('file', file);
      const { data } = await api.post('/upload/marketplace', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
      setForm(f => ({ ...f, preview_url: data.url }));
      toast.success('Cover photo uploaded');
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Upload failed');
    } finally {
      setUploadingCover(false);
    }
  };

  const submit = async () => {
    if (!form.title.trim()) return toast.error('Title required');
    if (!form.content.trim() && form.category !== 'artwork') return toast.error('Content required for prompt packs');
    setBusy(true);
    try {
      await api.post('/marketplace/products', form);
      toast.success('Listing published!');
      onCreated && onCreated();
      onClose();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed');
    } finally { setBusy(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent data-testid="market-create-dialog" className="max-w-md max-h-[85vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle>Create a Listing</DialogTitle>
          <DialogDescription>Sell prompt packs, artwork, or templates. Jixxeo takes 10%, you keep 90%.</DialogDescription>
        </DialogHeader>
        <div className="space-y-3">
          <div>
            <Label className="text-xs">Title</Label>
            <Input data-testid="market-title" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} placeholder="e.g. Noir Night Prompt Pack" />
          </div>
          <div>
            <Label className="text-xs">Description</Label>
            <Textarea data-testid="market-description" rows={3} value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} placeholder="What's included?" />
          </div>
          <div>
            <Label className="text-xs">Category</Label>
            <div className="grid grid-cols-5 gap-1">
              {CATEGORIES.map(c => (
                <button
                  key={c.v}
                  data-testid={`market-cat-${c.v}`}
                  onClick={() => setForm(f => ({ ...f, category: c.v }))}
                  className={`p-2 rounded-lg text-[10px] font-bold flex flex-col items-center gap-1 border ${form.category === c.v ? 'bg-violet-500/20 border-violet-500/60 text-violet-200' : 'border-white/10 text-slate-400 hover:border-white/30'}`}
                >
                  <c.Icon className="w-3.5 h-3.5" />{c.label.split(' ')[0]}
                </button>
              ))}
            </div>
          </div>
          <div>
            <Label className="text-xs">Pricing</Label>
            <div className="grid grid-cols-2 gap-2">
              <button
                data-testid="market-currency-jxp"
                onClick={() => setForm(f => ({ ...f, currency: 'jxp' }))}
                className={`p-2 rounded-lg border text-sm font-bold flex items-center justify-center gap-1 ${form.currency === 'jxp' ? 'bg-amber-500/20 border-amber-400/60 text-amber-200' : 'border-white/10 text-slate-400'}`}
              >
                <Coins className="w-4 h-4" /> JXP
              </button>
              <button
                data-testid="market-currency-usd"
                onClick={() => setForm(f => ({ ...f, currency: 'usd' }))}
                className={`p-2 rounded-lg border text-sm font-bold flex items-center justify-center gap-1 ${form.currency === 'usd' ? 'bg-emerald-500/20 border-emerald-400/60 text-emerald-200' : 'border-white/10 text-slate-400'}`}
              >
                <DollarSign className="w-4 h-4" /> USD
              </button>
            </div>
            {form.currency === 'jxp' ? (
              <Input data-testid="market-price-jxp" className="mt-2" type="number" min="1" value={form.price_jxp} onChange={e => setForm(f => ({ ...f, price_jxp: +e.target.value }))} placeholder="JXP price" />
            ) : (
              <Input data-testid="market-price-usd" className="mt-2" type="number" min="0.50" step="0.01" value={form.price_usd} onChange={e => setForm(f => ({ ...f, price_usd: +e.target.value }))} placeholder="USD price (min $0.50)" />
            )}
            <div className="text-[10px] text-slate-500 mt-1">
              You'll earn {form.currency === 'jxp' ? `${Math.round((form.price_jxp || 0) * 0.9)} JXP` : `$${((form.price_usd || 0) * 0.9).toFixed(2)}`} per sale (after 10% Jixxeo fee).
            </div>
          </div>
          <div>
            <Label className="text-xs">Cover photo (optional)</Label>
            <div className="flex items-center gap-3 mt-1">
              <div className="w-20 h-20 rounded-lg bg-slate-800 border border-white/10 overflow-hidden flex-shrink-0 flex items-center justify-center">
                {form.preview_url ? (
                  <img src={form.preview_url} alt="cover" className="w-full h-full object-cover" />
                ) : (
                  <ImageIcon className="w-6 h-6 text-slate-600" />
                )}
              </div>
              <div className="flex-1 flex flex-col gap-1.5">
                <label
                  data-testid="market-cover-upload"
                  className="inline-flex items-center justify-center gap-2 px-3 py-2 rounded-lg bg-white/10 hover:bg-white/15 border border-white/15 text-xs font-semibold cursor-pointer"
                >
                  {uploadingCover
                    ? <><Loader2 className="w-3.5 h-3.5 animate-spin" /> Uploading…</>
                    : <><ImageIcon className="w-3.5 h-3.5" /> {form.preview_url ? 'Change cover photo' : 'Upload cover photo'}</>}
                  <input
                    data-testid="market-cover-input"
                    type="file"
                    accept="image/*"
                    capture="environment"
                    className="hidden"
                    onChange={e => pickCover(e.target.files?.[0])}
                  />
                </label>
                {form.preview_url && (
                  <button
                    type="button"
                    data-testid="market-cover-remove"
                    onClick={() => setForm(f => ({ ...f, preview_url: '' }))}
                    className="text-[11px] text-slate-400 hover:text-rose-300 text-left"
                  >Remove photo</button>
                )}
                <div className="text-[10px] text-slate-500">Pick from your camera roll or snap a new photo. Max 5 MB.</div>
              </div>
            </div>
          </div>
          <div>
            <Label className="text-xs">{form.category === 'artwork' ? 'High-res artwork URL' : 'Content (prompt list / template body)'}</Label>
            <Textarea data-testid="market-content" rows={5} value={form.content} onChange={e => setForm(f => ({ ...f, content: e.target.value }))} placeholder={form.category === 'artwork' ? 'Link to the high-resolution file' : 'The actual prompts / template that buyers will unlock'} />
            <div className="text-[10px] text-slate-500 mt-1">Buyers unlock this content immediately after purchase.</div>
          </div>
        </div>
        <DialogFooter>
          <Button data-testid="market-create-publish" onClick={submit} disabled={busy} className="gradient-brand text-white w-full">
            {busy ? <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Publishing…</> : 'Publish Listing'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default function Marketplace() {
  const { user } = useAuth();
  const nav = useNavigate();
  const [listings, setListings] = useState([]);
  const [filter, setFilter] = useState({ cat: '', cur: '' });
  const [sort, setSort] = useState('new');
  const [opened, setOpened] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = async () => {
    const params = new URLSearchParams();
    if (filter.cat) params.set('category', filter.cat);
    if (filter.cur) params.set('currency', filter.cur);
    if (sort) params.set('sort', sort);
    const { data } = await api.get(`/marketplace/products?${params.toString()}`);
    setListings(data);
  };
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [filter, sort]);

  return (
    <div className="max-w-6xl mx-auto space-y-5 pb-24">
      <header className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h1 className="font-display text-3xl font-black flex items-center gap-2">
            <Store className="w-7 h-7 text-fuchsia-400" /> <span className="gradient-text">Marketplace</span>
          </h1>
          <p className="text-sm text-slate-400">Buy and sell AI prompt packs, art, and templates. Pay with JXP or USD.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Link to="/marketplace/mine" data-testid="market-mine-link">
            <Button variant="outline" size="sm"><Package className="w-3.5 h-3.5 mr-1" /> My Listings & Purchases</Button>
          </Link>
          {user && (
            <Button data-testid="market-create-btn" onClick={() => setShowCreate(true)} className="gradient-brand text-white" size="sm">
              <Plus className="w-3.5 h-3.5 mr-1" /> Sell Something
            </Button>
          )}
        </div>
      </header>

      {/* Filters */}
      <Card className="glass p-3 flex flex-wrap gap-2 items-center">
        <Filter className="w-3.5 h-3.5 text-slate-400" />
        <button data-testid="market-filter-all" onClick={() => setFilter({ cat: '', cur: '' })}
          className={`text-xs px-2.5 py-1 rounded-full ${!filter.cat && !filter.cur ? 'gradient-brand text-white' : 'bg-white/5 text-slate-300'}`}>
          All
        </button>
        {CATEGORIES.map(c => (
          <button key={c.v} data-testid={`market-filter-${c.v}`}
            onClick={() => setFilter(f => ({ ...f, cat: f.cat === c.v ? '' : c.v }))}
            className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${filter.cat === c.v ? 'gradient-brand text-white' : 'bg-white/5 text-slate-300'}`}>
            <c.Icon className="w-3 h-3" /> {c.label}
          </button>
        ))}
        <div className="ml-auto flex gap-1">
          <button data-testid="market-currency-filter-jxp" onClick={() => setFilter(f => ({ ...f, cur: f.cur === 'jxp' ? '' : 'jxp' }))}
            className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${filter.cur === 'jxp' ? 'bg-amber-500/30 text-amber-200' : 'bg-white/5 text-slate-300'}`}>
            <Coins className="w-3 h-3" /> JXP
          </button>
          <button data-testid="market-currency-filter-usd" onClick={() => setFilter(f => ({ ...f, cur: f.cur === 'usd' ? '' : 'usd' }))}
            className={`text-xs px-2.5 py-1 rounded-full inline-flex items-center gap-1 ${filter.cur === 'usd' ? 'bg-emerald-500/30 text-emerald-200' : 'bg-white/5 text-slate-300'}`}>
            <DollarSign className="w-3 h-3" /> USD
          </button>
          <select data-testid="market-sort" value={sort} onChange={e => setSort(e.target.value)}
            className="text-xs px-2 py-1 rounded-full bg-white/5 border border-white/10">
            <option value="new">Newest</option>
            <option value="popular">Most sold</option>
            <option value="price_low">Price (low)</option>
          </select>
        </div>
      </Card>

      {/* Grid */}
      {listings.length === 0 ? (
        <Card className="glass p-10 text-center text-slate-500 text-sm">
          <Store className="w-10 h-10 mx-auto mb-3 opacity-30" />
          No listings yet. Be the first to sell something!
        </Card>
      ) : (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {listings.map(p => (
            <ListingCard key={p.id} p={p} onOpen={(prod) => {
              // Fetch full product with content gating
              api.get(`/marketplace/products/${prod.id}`).then(r => setOpened(r.data));
            }} />
          ))}
        </div>
      )}

      {opened && <PurchaseDialog product={opened} user={user} onClose={() => setOpened(null)} onPurchased={() => { load(); api.get(`/marketplace/products/${opened.id}`).then(r => setOpened(r.data)); }} />}
      <CreateListingDialog open={showCreate} onClose={() => setShowCreate(false)} onCreated={load} />
    </div>
  );
}
